import { useState, useEffect } from "react";
import { IntegrationCard } from "@/components/integrations/IntegrationCard";
import { IntegrationDialog } from "@/components/integrations/IntegrationDialog";
import {
  useIntegrations,
  IntegrationProvider,
} from "@/hooks/useIntegrations";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Plug } from "lucide-react";
import { ProductSelector } from "@/components/ProductSelector";

// Import logos
import stripeLogo from "@/assets/integrations/stripe-logo.png";
import paypalLogo from "@/assets/integrations/paypal-logo.png";
import mercadopagoLogo from "@/assets/integrations/mercadopago-logo.png";
import pagseguroLogo from "@/assets/integrations/pagseguro-logo.png";
import hotmartLogo from "@/assets/integrations/hotmart-logo.png";
import kiwifyLogo from "@/assets/integrations/kiwify-logo.png";
import pagarmeLogo from "@/assets/integrations/pagarme-logo.png";
import vindiLogo from "@/assets/integrations/vindi-logo.png";
import iuguLogo from "@/assets/integrations/iugu-logo.png";
import eduzzLogo from "@/assets/integrations/eduzz-logo.png";

const integrationsList = [
  {
    provider: "stripe" as IntegrationProvider,
    name: "Stripe",
    description: "Gateway de pagamentos internacional",
    logoUrl: stripeLogo,
    available: true,
  },
  {
    provider: "paypal" as IntegrationProvider,
    name: "PayPal",
    description: "Pagamentos online seguros",
    logoUrl: paypalLogo,
    available: true,
  },
  {
    provider: "mercadopago" as IntegrationProvider,
    name: "Mercado Pago",
    description: "Solução de pagamentos para América Latina",
    logoUrl: mercadopagoLogo,
    available: true,
  },
  {
    provider: "pagseguro" as IntegrationProvider,
    name: "PagSeguro",
    description: "Gateway de pagamentos brasileiro",
    logoUrl: pagseguroLogo,
    available: true,
  },
  {
    provider: "hotmart" as IntegrationProvider,
    name: "Hotmart",
    description: "Plataforma de infoprodutos",
    logoUrl: hotmartLogo,
    available: true,
  },
  {
    provider: "kiwify" as IntegrationProvider,
    name: "Kiwify",
    description: "Checkout e gestão de infoprodutos",
    logoUrl: kiwifyLogo,
    available: true,
  },
  {
    provider: "pagarme" as IntegrationProvider,
    name: "Pagar.me",
    description: "Gateway de pagamentos brasileiro",
    logoUrl: pagarmeLogo,
    available: true,
  },
  {
    provider: "vindi" as IntegrationProvider,
    name: "Vindi",
    description: "Gestão de recorrência e cobranças",
    logoUrl: vindiLogo,
    available: true,
  },
  {
    provider: "iugu" as IntegrationProvider,
    name: "Iugu",
    description: "Gestão financeira e pagamentos",
    logoUrl: iuguLogo,
    available: true,
  },
  {
    provider: "eduzz" as IntegrationProvider,
    name: "Eduzz",
    description: "Plataforma de vendas digitais",
    logoUrl: eduzzLogo,
    available: true,
  },
];

const Integrations = () => {
  const [selectedProvider, setSelectedProvider] = useState<IntegrationProvider | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    document.title = 'Dashi | Integrações';
  }, []);

  const {
    integrations,
    isLoading,
    error,
    connectIntegration,
    disconnectIntegration,
    deleteIntegration,
    syncIntegration,
  } = useIntegrations();

  const handleConnect = (provider: IntegrationProvider) => {
    setSelectedProvider(provider);
    setDialogOpen(true);
  };

  const handleConfigure = (provider: IntegrationProvider) => {
    setSelectedProvider(provider);
    setDialogOpen(true);
  };

  const getIntegrationForProvider = (provider: IntegrationProvider) => {
    return integrations.find((int) => int.provider === provider as string);
  };

  const handleConnectIntegration = (
    provider: IntegrationProvider,
    credentials: any,
    productId?: string
  ) => {
    connectIntegration({ provider, credentials, productId });
  };

  if (error) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Erro ao carregar integrações. Por favor, tente novamente.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Plug className="h-8 w-8" />
              Integrações
            </h1>
            <p className="text-muted-foreground mt-1">
              Conecte seus gateways de pagamento para sincronização automática
            </p>
          </div>
          <ProductSelector />
        </div>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-40" />
            ))}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {integrationsList.map((item) => {
              const integration = getIntegrationForProvider(item.provider);
              return (
                <IntegrationCard
                  key={item.provider}
                  name={item.name}
                  description={item.description}
                  provider={item.provider}
                  logoUrl={item.logoUrl}
                  integration={integration}
                  isAvailable={item.available}
                  onConnect={() => handleConnect(item.provider)}
                  onDisconnect={disconnectIntegration}
                  onDelete={deleteIntegration}
                  onSync={syncIntegration}
                  onConfigure={() => handleConfigure(item.provider)}
                />
              );
            })}
          </div>
        )}
      </div>

      {selectedProvider && (
        <IntegrationDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          provider={selectedProvider}
          onConnect={handleConnectIntegration}
        />
      )}
    </div>
  );
};

export default Integrations;
